import React, { useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';

import { L10nAddress } from '@travel/i18n';
import { Station } from '@travel/icons/service';
import { ArrowRight, Spot, SpotOutline } from '@travel/icons/ui';
import { useTranslation } from '@travel/traveler-core/hooks';
import useDeviceType, { MOBILE_SCREEN } from '@travel/traveler-core/hooks/useDeviceType';
import useDialogHandler from '@travel/traveler-core/hooks/useDialogHandler';
import useKeyboard from '@travel/traveler-core/hooks/useKeyboard';
import { IconText } from '@travel/ui';
import { cx, isNotEmptyArray } from '@travel/utils';

import ButtonNoStyle from 'components/ButtonNoStyle';
import Dialog from 'components/Dialog';
import getStaticMapURL from 'components/LocationInformation/getStaticUrl';
import LocationMap from 'components/LocationMap';
import MapOptionPopup from 'components/MapOptionPopup';
import Media from 'components/Media';
import NearbyTerminals from 'components/NearbyTerminals';

import { getItem } from 'store/providerInformation/selectors';

import { Translate } from 'core/translate';

import styles from './basicInformation.module.scss';

const STATIC_MAP_SIZE_SP = '375x170';
const STATIC_MAP_SIZE_PC = '1128x460';

// mobile only
function LocationDetail(props: {
  className?: string;
  /** parent page name for RAT popups */
  pageName: 'hotel:rooms_and_plans_dated_list' | 'hotel:rooms_and_plans_undated_list';
  /** Flag to hide Map */
  hideMap?: boolean;
  /** Flag to hide Location */
  hideLocation?: boolean;
  /** Zoom level to show the Map */
  zoomLevel?: number;
}) {
  const provider = useSelector(getItem);
  const deviceType = useDeviceType();
  const isMobile = deviceType === MOBILE_SCREEN;
  const mapAlt = useTranslation({ id: 'Provider_Info.Navigation.Alt.Map' });

  const staticMapSize = isMobile ? STATIC_MAP_SIZE_SP : STATIC_MAP_SIZE_PC;

  const { isOpen: isOpenMap, onClose: onCloseMap, onOpen: onOpenMap } = useDialogHandler(false);
  const {
    isOpen: isOpenOptions,
    onClose: onCloseOptions,
    onOpen: onOpenOptions,
  } = useDialogHandler(false);

  const {
    isOpen: isOpenLocationInfo,
    onClose: onCloseLocationInfo,
    onOpen: onOpenLocationInfo,
  } = useDialogHandler(false);

  const [staticMap, setStaticMap] = useState('');

  const { address, nearbyTerminals, location } = provider;
  const { hideMap, hideLocation, zoomLevel } = props;

  const addressText = (
    <L10nAddress
      address={address?.addressLine}
      postcode={address?.postCode}
      country={address?.country}
    />
  );

  useEffect(() => {
    getStaticMapURL({ ...location, zoomLevel }, staticMapSize).then((res) => {
      setStaticMap(res);
    });
  }, [location, zoomLevel, staticMapSize]);

  const onKeyDownOptions = useKeyboard(
    useMemo(
      () => ({
        Submit: () => onOpenOptions(),
        Escape: () => onCloseOptions(),
      }),
      [onOpenOptions, onCloseOptions],
    ),
  );

  const onMapKeyDown = useKeyboard(
    useMemo(
      () => ({
        Submit: () => onOpenMap(),
      }),
      [onOpenMap],
    ),
  );

  return (
    <div
      className={cx(props.className)}
      data-testid="providerInfo-basicInformation-locationDetail-wrapper"
    >
      {!hideMap && (
        <button
          className={styles.map}
          onClick={onOpenMap}
          onKeyDown={onMapKeyDown}
          data-testid="providerInfo-basicInformation-locationDetail"
        >
          <Media media={{ url: staticMap, alt: mapAlt }} isDisableLazyload={false} isMediaOnly />
        </button>
      )}

      {!hideLocation && (
        <div className={cx(styles.infoContainer)}>
          {addressText && (
            <button
              onClick={onOpenOptions}
              className={styles.addressBox}
              onKeyDown={onKeyDownOptions}
            >
              <IconText
                className={styles.address}
                text={addressText}
                spaceBetween={12}
                icon={isMobile ? <Spot size={24} /> : <SpotOutline size={16} />}
                tabIndex={0}
                data-testid="providerInfo-basicInformation-locationDetail-addressLink"
              />
              {isOpenOptions && (
                <MapOptionPopup
                  className={styles.mapOption}
                  location={location}
                  isOpen={isOpenOptions}
                  onClose={onCloseOptions}
                  onClickItem={onCloseOptions}
                />
              )}
            </button>
          )}
          {isMobile && isNotEmptyArray(nearbyTerminals) && (
            <ButtonNoStyle
              onClick={onOpenLocationInfo}
              data-testid="locationDetail-nearbyTerminals-button"
            >
              <div className={styles.nearbyContainer}>
                <IconText
                  text={<NearbyTerminals {...nearbyTerminals[0]} />}
                  spaceBetween={16}
                  icon={<Station size={24} />}
                />

                <ArrowRight size={16} color={'cilantro'} />
              </div>
            </ButtonNoStyle>
          )}
        </div>
      )}
      {isOpenMap && (
        <LocationMap
          pageName={props.pageName}
          item={provider}
          isOpen={isOpenMap}
          onClose={onCloseMap}
        />
      )}

      <Dialog
        title={<Translate id="Traveler_Common.Terminal_Title" />}
        isOpen={isOpenLocationInfo}
        isSEOContentIndexedOnHidden={true}
        onClose={onCloseLocationInfo}
        hasEnableBack={false}
        isUseContentFlexBox={false}
        contentClassName={styles.dialogContent}
        hasAnimation={true}
      >
        <Station size={24} />
        <div
          className={styles.locationContent}
          data-testid="locationDetail-nearbyTerminals-wrapper"
        >
          {nearbyTerminals?.map((item) => (
            <NearbyTerminals key={item.name} {...item} className={styles.locationItem} />
          ))}
        </div>
      </Dialog>
    </div>
  );
}

export default LocationDetail;
